// components/userManagement/UserCreateTab.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  MenuItem,
  Paper,
  Typography,
  Alert,
  Stack,
  CircularProgress,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { createUser, clearLastCreatedUser } from '../../slices/usersSlice';
import { useToast } from '../../context/ToastContext';
import SearchableSelect from '../SearchableSelect';
import axios from 'axios';

const BASE_URL = `${process.env.REACT_APP_API_BASE_URL}/api`;

const UserCreateTab = ({ onSuccess }) => {
  const dispatch = useDispatch();
  const { showToast } = useToast();
  const { loading, lastCreatedUser } = useSelector((state) => state.users);
  const [formData, setFormData] = useState({
    role: '',
    employee_id: '',
    student_id: '',
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    username: '',
  });
  const [employees, setEmployees] = useState([]);
  const [allEmployees, setAllEmployees] = useState([]); // Store all employees
  const [students, setStudents] = useState([]);
  const [credentials, setCredentials] = useState(null);

  useEffect(() => {
    // Fetch employees and students for dropdowns
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('accessToken');
        const headers = { 'Authorization': `Bearer ${token}` };
        
        const [empRes, stuRes] = await Promise.all([
          axios.get(`${BASE_URL}/employees/minimal/`, { headers }),
          axios.get(`${BASE_URL}/students/`, { headers }),
        ]);
        
        const employeesData = empRes.data || [];
        setAllEmployees(employeesData); // Store all employees
        setEmployees(employeesData); // Initially show all
        setStudents(stuRes.data || []);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, []);
  
  // Filter employees based on selected role
  useEffect(() => {
    if (formData.role === 'teacher') {
      // Show only teachers when role is 'teacher'
      const teachers = allEmployees.filter(emp => emp.is_teacher === true);
      setEmployees(teachers);
    } else if (formData.role === 'employee') {
      // Show all employees when role is 'employee'
      setEmployees(allEmployees);
    } else {
      // Reset to all employees when role is cleared or changed
      setEmployees(allEmployees);
    }
  }, [formData.role, allEmployees]);

  useEffect(() => {
    if (lastCreatedUser && lastCreatedUser.generated_username) {
      setCredentials({
        username: lastCreatedUser.generated_username,
        password: lastCreatedUser.generated_password,
      });
      dispatch(clearLastCreatedUser());
    }
  }, [lastCreatedUser, dispatch]);

  const handleChange = async (e) => {
    const { name, value } = e.target;
    
    // Clear linked entity when role changes
    if (name === 'role') {
      setFormData(prev => ({
        ...prev,
        role: value,
        employee_id: '',
        student_id: '',
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        username: '',
      }));
      return;
    }
    
    // Password is auto-generated from phone, no need to update it separately
    
    // Auto-fill fields when employee is selected
    if (name === 'employee_id' && value) {
      const employee = employees.find(emp => emp.id === value);
      if (employee) {
        // Fetch full employee details
        try {
          const token = localStorage.getItem('accessToken');
          const headers = { 'Authorization': `Bearer ${token}` };
          const response = await axios.get(`${BASE_URL}/employees/${value}/`, { headers });
          const empData = response.data;
          
          // Generate username from employee_id if available
          const username = empData.employee_id || `emp_${value.substring(0, 8)}`;
          
          const phone = empData.phone_number || '';
          setFormData(prev => ({
            ...prev,
            employee_id: value,
            first_name: empData.first_name || employee.first_name || '',
            last_name: empData.last_name || employee.last_name || '',
            email: empData.email || '',
            phone: phone,
            username: username,
          }));
        } catch (error) {
          // Fallback to minimal data
          const username = employee.employee_id || `emp_${value.substring(0, 8)}`;
          setFormData(prev => ({
            ...prev,
            employee_id: value,
            first_name: employee.first_name || '',
            last_name: employee.last_name || '',
            email: '',
            phone: '',
            username: username,
          }));
        }
        return;
      }
    }
    
    // Auto-fill fields when student is selected
    if (name === 'student_id' && value) {
      const student = students.find(stu => stu.id === value);
      if (student) {
        // Fetch full student details
        try {
          const token = localStorage.getItem('accessToken');
          const headers = { 'Authorization': `Bearer ${token}` };
          const response = await axios.get(`${BASE_URL}/students/${value}/`, { headers });
          const stuData = response.data;
          
          // Generate username from student_id if available
          const username = stuData.student_id || `student_${value.substring(0, 8)}`;
          
          const phone = stuData.parent_phone || student.parent_phone || '';
          setFormData(prev => ({
            ...prev,
            student_id: value,
            first_name: stuData.first_name || student.first_name || '',
            last_name: stuData.last_name || stuData.second_name || student.second_name || student.last_name || '',
            email: stuData.parent_email || student.parent_email || '',
            phone: phone,
            username: username,
          }));
        } catch (error) {
          // Fallback to minimal data
          const username = student.student_id || `student_${value.substring(0, 8)}`;
          const phone = student.parent_phone || '';
          setFormData(prev => ({
            ...prev,
            student_id: value,
            first_name: student.first_name || '',
            last_name: student.last_name || student.second_name || '',
            email: student.parent_email || '',
            phone: phone,
            username: username,
          }));
        }
        return;
      }
    }
    
    // Regular field update
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const userData = {
        ...formData,
        employee_id: formData.employee_id || null,
        student_id: formData.student_id || null,
        username: formData.username || null, // If username is provided, use it; otherwise backend will generate
      };
      
      const result = await dispatch(createUser(userData)).unwrap();
      showToast('تم إنشاء المستخدم بنجاح', 'success');
      setFormData({
        role: '',
        employee_id: '',
        student_id: '',
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        username: '',
      });
      onSuccess();
    } catch (error) {
      const errorMsg = error.detail || error.message || 'حدث خطأ أثناء إنشاء المستخدم';
      showToast(errorMsg, 'error');
    }
  };

  const roleLabels = {
    manager: 'مدير',
    employee: 'موظف',
    teacher: 'معلم',
    parent: 'ولي أمر',
    student: 'طالب',
  };

  return (
    <Box sx={{ p: 3 }}>
      <form onSubmit={handleSubmit}>
        <Stack spacing={3}>
          <TextField
            select
            label="الصلاحية"
            name="role"
            value={formData.role}
            onChange={handleChange}
            required
            fullWidth
          >
            {Object.entries(roleLabels).map(([value, label]) => (
              <MenuItem key={value} value={value}>{label}</MenuItem>
            ))}
          </TextField>

          {(formData.role === 'teacher' || formData.role === 'employee') && (
            <SearchableSelect
              label="الموظف"
              options={employees.map((emp) => ({
                id: emp.id,
                label: emp.full_name || `${emp.first_name} ${emp.last_name}`,
              }))}
              value={formData.employee_id}
              onChange={(value) => handleChange({ target: { name: 'employee_id', value } })}
              required
            />
          )}

          {(formData.role === 'student' || formData.role === 'parent') && (
            <SearchableSelect
              label="الطالب"
              options={students.map((stu) => ({
                id: stu.id,
                label: `${stu.first_name} ${stu.last_name || stu.second_name || ''}`.trim(),
              }))}
              value={formData.student_id}
              onChange={(value) => handleChange({ target: { name: 'student_id', value } })}
              required
            />
          )}

          <TextField
            label="اسم المستخدم"
            name="username"
            value={formData.username}
            onChange={handleChange}
            fullWidth
            helperText={formData.employee_id || formData.student_id ? "تم توليده تلقائياً (يمكن التعديل)" : "سيتم توليده تلقائياً عند إنشاء المستخدم"}
          />

          <TextField
            label="الاسم الأول"
            name="first_name"
            value={formData.first_name}
            onChange={handleChange}
            fullWidth
            helperText={formData.employee_id || formData.student_id ? "تم ملؤه تلقائياً" : ""}
          />

          <TextField
            label="اسم العائلة"
            name="last_name"
            value={formData.last_name}
            onChange={handleChange}
            fullWidth
            helperText={formData.employee_id || formData.student_id ? "تم ملؤه تلقائياً" : ""}
          />

          <TextField
            label="البريد الإلكتروني"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            fullWidth
            helperText={formData.employee_id || formData.student_id ? "تم ملؤه تلقائياً" : ""}
          />

          <TextField
            label="رقم الهاتف"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            fullWidth
            helperText={formData.employee_id || formData.student_id ? "تم ملؤه تلقائياً" : ""}
          />

          <TextField
            label="كلمة المرور (معاينة)"
            type="text"
            value={formData.phone ? formData.phone.replace(/\D/g, '') : ''}
            fullWidth
            helperText={formData.phone ? "سيتم استخدام رقم الهاتف (الأرقام فقط) ككلمة مرور تلقائياً" : "أدخل رقم الهاتف أولاً"}
            InputProps={{
              readOnly: true, // Read-only preview
            }}
            sx={{
              '& .MuiInputBase-input': {
                backgroundColor: 'rgba(0, 0, 0, 0.02)',
                cursor: 'default',
              }
            }}
          />

          {credentials && (
            <Alert severity="success">
              <Typography variant="h6">تم إنشاء المستخدم بنجاح!</Typography>
              <Typography>اسم المستخدم: <strong>{credentials.username}</strong></Typography>
              <Typography>كلمة المرور: <strong>{credentials.password}</strong></Typography>
              <Typography variant="caption" color="error">
                يرجى حفظ هذه المعلومات - لن يتم عرضها مرة أخرى
              </Typography>
            </Alert>
          )}

          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            fullWidth
            size="large"
          >
            {loading ? <CircularProgress size={24} /> : 'إنشاء مستخدم'}
          </Button>
        </Stack>
      </form>
    </Box>
  );
};

export default UserCreateTab;
